import React, { useEffect, useState } from "react";
import YhModal from "../../../../ui/YhModal";
import YhSelect from "../../../../ui/YhSelect";
import YhButton from "../../../../ui/YhButton";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../../../helpers/methods";
import { listFeedbackForms } from "../../../Lead/components/stepper/StepperService";
import { fetchAuthKeyTemplates } from "../../../../services/organizationService";
import urls from "../../../../global/constants/UrlConstants";
import { getCallParams, makeCall } from "../../../../utils/service";

type Props = {
  open: boolean;
  onClose: () => void;
  onSent?: () => void;
  leadIds: string[];
  organizationId: string;
};

const FeedbackBulkSendModal = ({
  open,
  onClose,
  onSent,
  leadIds,
  organizationId,
}: Props) => {
  const [forms, setForms] = useState<
    { _id: string; form_name: string; formScope?: string }[]
  >([]);
  const [templates, setTemplates] = useState<
    { wid: string; name: string; label: string }[]
  >([]);
  const [formId, setFormId] = useState("");
  const [templateWid, setTemplateWid] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const [formsRes, tplRes] = await Promise.all([
          listFeedbackForms(),
          organizationId && organizationId !== "all"
            ? fetchAuthKeyTemplates(organizationId, "whatsapp")
            : Promise.resolve({ data: [] }),
        ]);
        const formList = Array.isArray(formsRes?.data) ? formsRes.data : [];
        setForms(
          formList.map((f: any) => ({
            _id: String(f._id),
            form_name: f.form_name || "Feedback form",
            formScope: f.formScope || "",
          }))
        );
        const raw = tplRes?.data || [];
        setTemplates(
          (Array.isArray(raw) ? raw : [])
            .map((t: any) => {
              const wid = String(t.wid || t.id || t.template_id || "");
              const name = String(t.name || t.template_name || wid);
              if (!wid) return null;
              return { wid, name, label: `${name} (${wid})` };
            })
            .filter(Boolean) as { wid: string; name: string; label: string }[]
        );
      } catch (e: any) {
        openErrorNotification(e?.message || "Failed to load options");
      } finally {
        setLoading(false);
      }
    })();
  }, [open, organizationId]);

  const onSend = async () => {
    if (!leadIds.length) {
      openErrorNotification("Select at least one lead");
      return;
    }
    if (!formId) {
      openErrorNotification("Select a feedback form");
      return;
    }
    if (!templateWid) {
      openErrorNotification("Select a WhatsApp template");
      return;
    }
    const tpl = templates.find((t) => t.wid === templateWid);
    setSending(true);
    try {
      const callParams = await getCallParams("POST", {
        leadIds,
        formId,
        templateWid,
        templateName: tpl?.name || "",
        organizationId:
          organizationId && organizationId !== "all"
            ? organizationId
            : undefined,
        feedbackUrlMode: "path",
      });
      const res = await makeCall(
        `${urls.url_prod}/feedback/bulk-send-whatsapp`,
        callParams
      );
      if (!res?.success) {
        openErrorNotification(res?.message || "Send failed");
        return;
      }
      openSuccessNotification(
        res.message ||
          `Sent ${res?.data?.sent || 0}/${res?.data?.total || 0} messages`
      );
      onSent?.();
      onClose();
    } catch (e: any) {
      openErrorNotification(e?.message || "Unable to send feedback");
    } finally {
      setSending(false);
    }
  };

  return (
    <YhModal
      open={open}
      onClose={onClose}
      title="Send feedback on WhatsApp"
      width={480}
    >
      <div className="flex flex-col gap-4 p-1">
        <p className="text-sm text-[#6B7280]">
          Selected leads:{" "}
          <span className="font-semibold text-[#111827]">{leadIds.length}</span>
          . Each customer gets a unique no-shared token link in the template
          Visit website {"{{1}}"}.
        </p>

        <YhSelect
          label="Feedback form"
          value={formId}
          disabled={loading}
          onChange={(e: any) => setFormId(e.target.value)}
          options={[
            {
              label: forms.length ? "Select form" : "No feedback forms",
              value: "",
            },
            ...forms.map((f) => ({
              label: `${f.form_name}${
                f.formScope === "project" ? " · project" : " · org"
              }`,
              value: f._id,
            })),
          ]}
        />

        <YhSelect
          label="WhatsApp template"
          value={templateWid}
          disabled={loading || !organizationId || organizationId === "all"}
          onChange={(e: any) => setTemplateWid(e.target.value)}
          options={[
            {
              label:
                !organizationId || organizationId === "all"
                  ? "Select an organization filter first"
                  : templates.length
                    ? "Select template"
                    : "No templates found",
              value: "",
            },
            ...templates.map((t) => ({ label: t.label, value: t.wid })),
          ]}
        />

        <div className="rounded-xl border border-[#F9F0F4] bg-[#F8FAFF] px-3 py-2.5 text-xs text-[#475569]">
          Template website base should be your public site host (e.g.
          localhost:3000). Dynamic path is injected per lead automatically.
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <YhButton variant="secondary" onClick={onClose} disabled={sending}>
            Cancel
          </YhButton>
          <YhButton onClick={onSend} loading={sending} disabled={loading}>
            Send WhatsApp
          </YhButton>
        </div>
      </div>
    </YhModal>
  );
};

export default FeedbackBulkSendModal;
